import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { states, districts } from "../../data/locationData";

const CourtFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    applicantFullName: "",
    applicantFatherOrHusband: "",
    applicantAge: "",
    applicantGender: "",
    applicantOccupation: "",
    applicantContact: "",
    applicantEmail: "",
    applicantPresentAddress: "",
    applicantPermanentAddress: "",
    opponentFullName: "",
    opponentRelationship: "",
    opponentRelationshipOther: "",
    opponentAddress: "",
    opponentRole: "",
    applicationType: "",
    caseTitle: "",
    caseTitleOther: "",
    caseDescription: "",
    caseFacts: "",
    caseRelief: "",
    previousCase: "No",
    previousCaseFile: null,
    courtState: stateVal || "",
    courtDistrict: district || "",
    courtJurisdiction: "",
    courtType: "",
    courtName: "",
    isUrgent: "No",
    advocateName: "",
    advocateEnrollment: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCourtStateSelect = (e) => {
    const nextState = e.target.value;
    setFormData((prev) => ({
      ...prev,
      courtState: nextState,
      courtDistrict: "",
    }));
  };

  const handleCourtDistrictSelect = (e) => {
    setFormData((prev) => ({
      ...prev,
      courtDistrict: e.target.value,
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Applicant Details:
Full Name: ${formData.applicantFullName}
Father's/Husband's Name: ${formData.applicantFatherOrHusband}
Age: ${formData.applicantAge}
Gender: ${formData.applicantGender}
Occupation: ${formData.applicantOccupation}
Contact: ${formData.applicantContact}
Email: ${formData.applicantEmail}
Present Address: ${formData.applicantPresentAddress}
Permanent Address: ${formData.applicantPermanentAddress}

👥 Opponent Details:
Full Name: ${formData.opponentFullName}
Relationship: ${formData.opponentRelationshipOther || formData.opponentRelationship}
Address: ${formData.opponentAddress}
Role in Matter: ${formData.opponentRole}

⚖️ Case Details:
Application Type: ${formData.applicationType}
Case Title: ${formData.caseTitleOther || formData.caseTitle}
Case Description: ${formData.caseDescription}
Case Facts: ${formData.caseFacts}
Relief Sought: ${formData.caseRelief}
Previous Case: ${formData.previousCase}

🏛️ Court Details:
Court State: ${formData.courtState}
Court District: ${formData.courtDistrict}
Court Jurisdiction: ${formData.courtJurisdiction}
Court Type: ${formData.courtType}
Court Name: ${formData.courtName}
Is Urgent: ${formData.isUrgent}

👨‍💼 Advocate Details:
Advocate Name: ${formData.advocateName}
Advocate Enrollment: ${formData.advocateEnrollment}
`.trim();

    // Add extracted document content if available
    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:\n`;
      uploadedDocuments.forEach((doc, index) => {
        detailsText += `\nDocument ${index + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}\n`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
        draftType: "court",
        applicantName: formData.applicantFullName,
        advocateName: formData.advocateName,
        courtName: formData.courtName,
        uploadedDocuments,
        extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Applicant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="applicantFullName"
            value={formData.applicantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="applicantFatherOrHusband"
            value={formData.applicantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="applicantAge"
            value={formData.applicantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="applicantGender"
            value={formData.applicantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="applicantOccupation"
            value={formData.applicantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="applicantContact"
            value={formData.applicantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="applicantEmail"
            value={formData.applicantEmail}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Present Address *</label>
          <textarea
            name="applicantPresentAddress"
            value={formData.applicantPresentAddress}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Permanent Address *</label>
          <textarea
            name="applicantPermanentAddress"
            value={formData.applicantPermanentAddress}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Opponent Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Opponent Full Name *</label>
          <input
            type="text"
            name="opponentFullName"
            value={formData.opponentFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Relationship to Applicant *</label>
          <select
            name="opponentRelationship"
            value={formData.opponentRelationship}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Relationship</option>
            <option value="Family Member">Family Member</option>
            <option value="Neighbour">Neighbour</option>
            <option value="Business Partner">Business Partner</option>
            <option value="Employee">Employee</option>
            <option value="Employer">Employer</option>
            <option value="Friend">Friend</option>
            <option value="Tenant">Tenant</option>
            <option value="Landlord">Landlord</option>
            <option value="Stranger">Stranger</option>
            <option value="Government Official">Government Official</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
      </div>
      {formData.opponentRelationship === "Other" && (
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Specify Relationship *</label>
            <input
              type="text"
              name="opponentRelationshipOther"
              value={formData.opponentRelationshipOther}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      )}
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Opponent Address *</label>
          <textarea
            name="opponentAddress"
            value={formData.opponentAddress}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Role in Matter *</label>
          <select
            name="opponentRole"
            value={formData.opponentRole}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Role</option>
            <option value="Defendant">Defendant</option>
            <option value="Respondent">Respondent</option>
            <option value="Accused">Accused</option>
            <option value="Opposite Party">Opposite Party</option>
            <option value="Co-defendant">Co-defendant</option>
            <option value="Third Party">Third Party</option>
            <option value="Intervener">Intervener</option>
            <option value="Witness">Witness</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        🏛️ Court & Legal Details
      </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Court State *</label>
            {(states[formData.country] || []).length > 0 ? (
              <select
                name="courtState"
                value={formData.courtState}
                onChange={handleCourtStateSelect}
                required
                disabled={!formData.country}
              >
                <option value="">Select State</option>
                {(states[formData.country] || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="courtState"
                value={formData.courtState}
                onChange={handleInputChange}
                required
              />
            )}
          </div>
          <div className="form-page-col">
            <label>Court District *</label>
            {(() => {
              const hasStateList = (states[formData.country] || []).length > 0;
              const courtDistrictOptions = districts[formData.courtState] || [];

              if (!hasStateList) {
                return (
                  <input
                    type="text"
                    name="courtDistrict"
                    value={formData.courtDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              if (formData.courtState && courtDistrictOptions.length === 0) {
                return (
                  <input
                    type="text"
                    name="courtDistrict"
                    value={formData.courtDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              return (
                <select
                  name="courtDistrict"
                  value={formData.courtDistrict}
                  onChange={handleCourtDistrictSelect}
                  required
                  disabled={!formData.courtState}
                >
                  <option value="">Select District</option>
                  {courtDistrictOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Court Jurisdiction *</label>
            <select
              name="courtJurisdiction"
              value={formData.courtJurisdiction}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Jurisdiction</option>
              <option value="Civil">Civil</option>
              <option value="Criminal">Criminal</option>
              <option value="Family">Family</option>
              <option value="Commercial">Commercial</option>
              <option value="Revenue">Revenue</option>
              <option value="Constitutional">Constitutional</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Court Type *</label>
            <select
              name="courtType"
              value={formData.courtType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Court Type</option>
              <option value="District Court">District Court</option>
              <option value="High Court">High Court</option>
              <option value="Supreme Court">Supreme Court</option>
              <option value="Magistrate Court">Magistrate Court</option>
              <option value="Family Court">Family Court</option>
              <option value="Commercial Court">Commercial Court</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Court Name</label>
            <input
              type="text"
              name="courtName"
              value={formData.courtName}
              onChange={handleInputChange}
              placeholder="Name of the specific court"
            />
          </div>
          <div className="form-page-col">
            <label>Is this Urgent? *</label>
            <select
              name="isUrgent"
              value={formData.isUrgent}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Previous Case Filed? *</label>
            <select
              name="previousCase"
              value={formData.previousCase}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
          {formData.previousCase === "Yes" && (
            <div className="form-page-col">
              <label>Previous Case File</label>
              <input
                type="file"
                name="previousCaseFile"
                onChange={handleInputChange}
                accept=".pdf,.doc,.docx,.jpg,.png"
              />
            </div>
          )}
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Advocate Name</label>
            <input
              type="text"
              name="advocateName"
              value={formData.advocateName}
              onChange={handleInputChange}
              placeholder="Name of your advocate (if any)"
            />
          </div>
          <div className="form-page-col">
            <label>Advocate Enrollment Number</label>
            <input
              type="text"
              name="advocateEnrollment"
              value={formData.advocateEnrollment}
              onChange={handleInputChange}
              placeholder="Bar Council enrollment number"
            />
          </div>
        </div>
      </div>

    );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Case Details
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Application Type *</label>
            <select
              name="applicationType"
              value={formData.applicationType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Application Type</option>
              <option value="Civil Suit">Civil Suit</option>
              <option value="Criminal Case">Criminal Case</option>
              <option value="Family Matter">Family Matter</option>
              <option value="Property Dispute">Property Dispute</option>
              <option value="Contract Dispute">Contract Dispute</option>
              <option value="Employment Matter">Employment Matter</option>
              <option value="Consumer Complaint">Consumer Complaint</option>
              <option value="Writ Petition">Writ Petition</option>
              <option value="Appeal">Appeal</option>
              <option value="Revision">Revision</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Case Title *</label>
            <select
              name="caseTitle"
              value={formData.caseTitle}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Case Title</option>
              <option value="Suit for Declaration">Suit for Declaration</option>
              <option value="Suit for Injunction">Suit for Injunction</option>
              <option value="Suit for Recovery">Suit for Recovery</option>
              <option value="Suit for Partition">Suit for Partition</option>
              <option value="Suit for Possession">Suit for Possession</option>
              <option value="Divorce Petition">Divorce Petition</option>
              <option value="Maintenance Application">Maintenance Application</option>
              <option value="Bail Application">Bail Application</option>
              <option value="Other">Other (specify)</option>
            </select>
          </div>
        </div>
        {formData.caseTitle === "Other" && (
          <div className="form-page-row">
            <div className="form-page-col">
              <label>Specify Case Title *</label>
              <input
                type="text"
                name="caseTitleOther"
                value={formData.caseTitleOther}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        )}
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Case Description *</label>
            <textarea
              name="caseDescription"
              value={formData.caseDescription}
              onChange={handleInputChange}
              rows={4}
              placeholder="Brief description of your case..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Case Facts *</label>
            <textarea
              name="caseFacts"
              value={formData.caseFacts}
              onChange={handleInputChange}
              rows={4}
              placeholder="Detailed facts and circumstances of the case..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Relief Sought *</label>
            <textarea
              name="caseRelief"
              value={formData.caseRelief}
              onChange={handleInputChange}
              rows={3}
              placeholder="What relief or remedy are you seeking from the court?"
              required
            />
          </div>
        </div>
      </div>

      <div className="form-page-section">
        <div className="form-page-section-title">
          📄 Supporting Documents
        </div>
        <p className="form-page-note">
          Upload relevant documents such as contracts, agreements, correspondence, evidence,
          previous court orders, or any other supporting materials. The system will extract
          text from these documents to enhance your legal draft.
        </p>
        <DocumentUpload
          onDocumentsChange={handleDocumentsChange}
          maxFiles={8}
          acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>

      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided is true and accurate *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Court Application</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional court application</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Court Application
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourtFormPage;
